'use client';

import { useState, useEffect, useMemo, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { getPickerIcon, PICKER_ICON_NAMES } from '@/lib/picker-icons';
import type { Project } from "dico-types";

const DEFAULT_COLOR = '#64748b';

const COLOR_PRESETS = [
  '#ef4444', // red
  '#f97316', // orange
  '#f59e0b', // amber
  '#eab308', // yellow
  '#84cc16', // lime
  '#22c55e', // green
  '#10b981', // emerald
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#0ea5e9', // sky
  '#3b82f6', // blue
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#a855f7', // purple
  '#d946ef', // fuchsia
  '#ec4899', // pink
  '#64748b'  // slate (default)
];

interface ProjectFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  /** If provided, we're editing this project. Otherwise, creating a new one. */
  project?: Project | null;
  onSave: (data: { name: string; description?: string; color?: string; icon?: string }) => Promise<void>;
}

export function ProjectForm({ open, onOpenChange, workspaceId, project, onSave }: ProjectFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [iconName, setIconName] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && project) {
      setName(project.name);
      setDescription(project.description || '');
      setColor(project.color || DEFAULT_COLOR);
      setIconName(project.icon || '');
    } else if (open && !project) {
      setName('');
      setDescription('');
      setColor(DEFAULT_COLOR);
      setIconName('');
    }
  }, [open, project]);

  const handleSave = async () => {
    if (!name.trim()) return;

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        color,
        icon: iconName || undefined
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save project:', error);
    } finally {
      setSaving(false);
    }
  };

  const iconType = iconName ? getPickerIcon(iconName) : null;

  const projectIconItems = useMemo(() => {
    const record: Record<string, ReactNode> = {};
    for (const name of PICKER_ICON_NAMES) {
      const t = getPickerIcon(name) ?? 'Circle';
      record[name] = (
        <div className="flex items-center gap-2">
          <MonoIcon type={t} />
          <span>{name}</span>
        </div>
      );
    }
    return record;
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{project ? 'Edit Project' : 'Create Project'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="project-name">Name</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Product Development"
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="project-description">Description (optional)</Label>
            <Textarea
              id="project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this project..."
              rows={3}
            />
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`h-8 w-8 rounded border-2 transition-all hover:scale-110 ${
                    color === preset ? 'border-foreground ring-2 ring-offset-2' : 'border-border'
                  }`}
                  style={{ backgroundColor: preset }}
                  onClick={() => setColor(preset)}
                  aria-label={`Select color ${preset}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-20"
              />
              <Input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#000000"
                className="flex-1 font-mono text-sm"
              />
            </div>
          </div>

          {/* Icon */}
          <div className="space-y-2">
            <Label htmlFor="project-icon">Icon (optional)</Label>
            <Select
              value={iconName}
              onValueChange={(value) => setIconName(value || '')}
              items={projectIconItems}
            >
              <SelectTrigger id="project-icon">
                <SelectValue placeholder="Select an icon">
                  {iconType && (
                    <div className="flex items-center gap-2">
                      <span style={{ color }}>
                        <MonoIcon type={iconType} />
                      </span>
                      <span>{iconName}</span>
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {PICKER_ICON_NAMES.map((name) => {
                  const iconType = getPickerIcon(name) ?? 'Circle';
                  return (
                    <SelectItem key={name} value={name}>
                      <div className="flex items-center gap-2">
                        <MonoIcon type={iconType} />
                        <span>{name}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Preview */}
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground mb-2">Preview</p>
            <div className="flex items-center gap-3">
              {iconType && (
                <span style={{ color }}>
                  <MonoIcon type={iconType} />
                </span>
              )}
              <div>
                <p className="font-medium">{name || 'Project Name'}</p>
                {description && (
                  <p className="text-sm text-muted-foreground">{description}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!name.trim() || saving}>
            {saving && <MonoIcon type="Loader2" className="mr-2 animate-spin" />}
            {project ? 'Save Changes' : 'Create Project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
